import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ChangeDetectorRef,
  inject,
  DestroyRef,
  ViewChild,
  ElementRef,
  TemplateRef,
  ViewContainerRef,
  HostListener,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Company, CrmTag, Partner } from '@app/shared';
import { GlobalValuesService } from '@app/core';
import { getSafeImageUrl, PartnerService } from '@app/core';
import { finalize } from 'rxjs';
import { ModifyPartnerDrawerComponent } from 'src/app/features/maestros/drawers/modify-partner-drawer/modify-partner-drawer.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { QuillEditorComponent } from 'ngx-quill';
import {
  hasMeaningfulLeadDescription,
  shouldDeferLeadFieldCloseToBlur,
} from './lead-description.utils';

@Component({
  selector: 'app-lead-content',
  standalone: false,
  templateUrl: './lead-content.component.html',
  styleUrl: './lead-content.component.less',
})
export class LeadContentComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() lead: any = null;
  @Input() editPermissions: boolean = false;
  @Input() isEditModeEnabled: boolean = false;
  @Input() editModeAction: 'none' | 'save' | 'cancel' = 'none';
  @Input() activeTabIndex: number = 0;
  @Input() documents: any[] = [];
  @Output() updateEvent = new EventEmitter<any>();
  @Output() editModeChange = new EventEmitter<boolean>();
  @Output() fieldUpdate = new EventEmitter<{
    path: string;
    value: any;
    previousValue?: any;
    error?: boolean;
    errorMessage?: string;
  }>();

  // Datos de referencia
  companies: Company[] = [];
  companiesList: Partner[] = []; // Lista de empresas cliente (isCompany = true)
  companiesListLoaded = false; // Evita mostrar '-' antes del valor real en Empresa Cliente
  contactList: Partner[] = []; // Lista de contactos filtrados por empresa cliente
  contactLoading: boolean = false; // Estado de carga de contactos
  tags: CrmTag[] = [];
  countries: any[] = [];
  states: any[] = [];
  stages: any[] = [];
  costCenters: any[] = [];

  // Estado de edición
  currentEditingField: string | null = null;
  backupFieldValues: { [key: string]: any } = {};
  tempFieldValues: { [key: string]: any } = {};
  afterViewInit: boolean = false;
  fieldErrors: { [key: string]: string } = {}; // Almacena mensajes de error por campo
  editingFieldValue: string = '';
  isAddingLink: boolean = false; // Evita guardar/cerrar por blur al abrir el modal de link (usado en template)
  quillModules: any = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ header: [1, 2, 3, false] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
      ],
      handlers: {
        link: (value: boolean) => {
          const quillInstance = this.leadQuillEditor?.quillEditor;
          if (!quillInstance) return;

          const range = quillInstance.getSelection(true);

          if (value) {
            // Agregar o editar link
            if (range && range.length > 0) {
              const formats = quillInstance.getFormat(range);
              const currentLink = formats['link'] as string | undefined;
              this.isAddingLink = true;
              this.showLinkModal(currentLink || '', quillInstance, {
                index: range.index,
                length: range.length,
              });
            } else {
              this.modalService.warning({
                nzTitle: 'Seleccione texto',
                nzContent:
                  'Por favor, seleccione el texto al que desea agregar el enlace.',
                nzCentered: true,
                nzWidth: 400,
              });
            }
          } else {
            if (range && range.length > 0) quillInstance.format('link', false);
          }
        },
      },
    },
  };

  // Propiedades computadas (en lugar de funciones en la vista)
  leadName: string = '-';
  leadNameForInput: string = '';
  currentLeadId: number = 0;
  companyName: string = '-';
  contactName: string = '-';
  commercialName: string = '-';
  salesTeamName: string = '-';
  priorityName: string = '-';
  priorityStars: boolean[] = [false, false, false, false];
  leadTags: CrmTag[] = [];
  tagIds: number[] = [];
  stateName: string = '-';
  countryName: string = '-';
  stagesForTeam: any[] = [];
  currentStageName: string = 'Nuevo';
  filteredUsersForTeam: any[] = []; // Comerciales filtrados por equipo
  intermediaryCompanyName: string = '-';
  costCenterName: string = '-';
  referencedByName: string = '-';

  /** Mantener últimos valores mostrados cuando un re-render trae el lead "flaco" (sin partner/user/team/etc.) */
  private displaySnapshot: Partial<
    Record<
      | 'leadName'
      | 'companyName'
      | 'contactName'
      | 'commercialName'
      | 'salesTeamName'
      | 'costCenterName'
      | 'intermediaryCompanyName'
      | 'stateName'
      | 'countryName'
      | 'referencedByName',
      string
    >
  > = {};
  private lastLeadId: number | null = null;

  ModifyPartnerDrawerComponent = ModifyPartnerDrawerComponent;

  @ViewChild('leadQuillEditor', { static: false })
  leadQuillEditor?: QuillEditorComponent;
  @ViewChild('linkModalTemplate', { static: false })
  linkModalTemplate!: TemplateRef<any>;
  @ViewChild('linkInput', { static: false })
  linkInput?: ElementRef<HTMLInputElement>;
  linkModalUrl: string = '';
  private linkModalQuill: any = null;
  private linkModalRange: { index: number; length: number } | null = null;
  private linkModalRef: any = null;

  protected readonly getSafeImageUrl = getSafeImageUrl;
  private destroyRef = inject(DestroyRef);
  protected readonly globalValuesService = inject(GlobalValuesService);
  private partnerService = inject(PartnerService);
  private modalService = inject(NzModalService);
  private viewContainerRef = inject(ViewContainerRef);
  protected readonly hasMeaningfulLeadDescription =
    hasMeaningfulLeadDescription;

  constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.loadReferenceData();
    this.updateComputedProperties();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detectar cuando se hace rollback desde saveAllPendingChanges (editModeAction = 'cancel')
    if (changes['editModeAction']) {
      const previousAction = changes['editModeAction'].previousValue;
      const currentAction = changes['editModeAction'].currentValue;

      // Si la acción es 'save', guardar el campo en edición antes de que el padre procese
      if (currentAction === 'save' && previousAction !== 'save') {
        if (this.isFieldEditing('description')) {
          this.saveEditingField('description');
        }
      }

      if (currentAction === 'cancel' && previousAction !== 'cancel') {
        // Verificar si lead tiene campos con error marcados (rollback después de error)
        const fieldsWithErrors = (this.lead as any)?.__fieldsWithErrors as
          | string[]
          | undefined;

        if (fieldsWithErrors && fieldsWithErrors.length > 0) {
          // Es un rollback después de un error al guardar - marcar errores
          console.log(
            '🔄 Rollback después de error - marcando campos con error:',
            fieldsWithErrors
          );

          fieldsWithErrors.forEach((fieldPath) => {
            this.fieldErrors[fieldPath] = 'Error al guardar';
          });

          this.cdr.detectChanges();
        } else {
          // Es un cancelar normal (botón Cancelar) - limpiar errores
          console.log('🔄 Cancelar normal - limpiando errores');
          this.fieldErrors = {};
          this.cdr.detectChanges();
        }
      }
    }

    // También detectar cuando lead cambia después de un rollback
    if (
      changes['lead'] &&
      !changes['lead'].firstChange &&
      this.editModeAction === 'cancel'
    ) {
      const fieldsWithErrors = (this.lead as any)?.__fieldsWithErrors as
        | string[]
        | undefined;

      if (fieldsWithErrors && fieldsWithErrors.length > 0) {
        console.log(
          '📋 Campos con error detectados en cambio de lead:',
          fieldsWithErrors
        );

        // Marcar cada campo como con error
        fieldsWithErrors.forEach((fieldPath) => {
          this.fieldErrors[fieldPath] = 'Error al guardar';
          console.log('🔴 Campo marcado con error:', fieldPath);
        });

        this.cdr.detectChanges();
      }
    }

    if (changes['lead']) {
      this.updateComputedProperties();
    }
    if (changes['isEditModeEnabled']) {
      // Solo hacer rollback cuando la acción sea "cancel".
      // Si se desactiva el modo edición por guardado, no debemos re-emitir el valor anterior,
      // porque eso puede causar una petición extra con el dato viejo.
      if (!this.isEditModeEnabled && this.currentEditingField) {
        if (this.editModeAction === 'cancel') {
          this.cancelFieldEdit(this.currentEditingField);
        } else {
          this.stopEditingField(this.currentEditingField);
        }
      }
      // Si se desactiva el modo de edición (cancelar), limpiar todos los errores
      if (!this.isEditModeEnabled) {
        this.fieldErrors = {};
        this.cdr.detectChanges();
      }
    }
  }

  getPartnerInitials(partner: any): string {
    if (!partner) return '?';
    const name = partner.name || '';
    const surName = partner.surName || '';
    let initials = '';
    if (name.trim()) initials += name.trim().charAt(0).toUpperCase();
    if (surName.trim()) initials += surName.trim().charAt(0).toUpperCase();
    return initials || '?';
  }

  getUserInitialsFromUser(user: any): string {
    if (!user) return '?';
    const partner = user.partner || {};
    const name = partner.name || user.name || '';
    const surName = partner.surName || user.surName || '';
    let initials = '';
    if (name.trim()) initials += name.trim().charAt(0).toUpperCase();
    if (surName.trim()) initials += surName.trim().charAt(0).toUpperCase();
    return initials || '?';
  }

  private clearEditState(): void {
    // Limpiar backups y valores temporales
    this.backupFieldValues = {};
    this.tempFieldValues = {};
    this.currentEditingField = null;
    // Limpiar también los errores cuando se cancela
    this.fieldErrors = {};
  }

  ngAfterViewInit(): void {
    this.afterViewInit = true;
  }

  loadReferenceData(): void {
    this.globalValuesService.companies$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((companies: Company[]) => {
        this.companies = companies || [];
        this.updateComputedProperties();
      });

    // Cargar empresas cliente (isCompany = true)
    this.partnerService
      .getCompanies()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((companies: Partner[]) => {
        this.companiesList = companies || [];
        this.companiesListLoaded = true;
        this.updateComputedProperties();
      });

    this.globalValuesService.crmTags$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tags: CrmTag[]) => {
        this.tags = tags || [];
        this.updateComputedProperties();
      });

    this.globalValuesService.countries$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((countries: any[]) => {
        this.countries = countries || [];
        this.updateComputedProperties();
      });

    this.globalValuesService.states$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((states: any[]) => {
        this.states = states || [];
        this.updateComputedProperties();
      });

    this.globalValuesService.stages$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((stages: any[]) => {
        this.stages = stages || [];
        this.updateComputedProperties();
      });

    this.globalValuesService.costCenters$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((costCenters: { id: number; name: string }[]) => {
        this.costCenters = costCenters || [];
        this.updateComputedProperties();
      });
  }

  updateComputedProperties(): void {
    if (!this.lead) return;

    const leadId = this.lead?.id ?? this.lead?.originalId ?? 0;
    const isSameLead = this.lastLeadId !== null && this.lastLeadId === leadId;
    if (!isSameLead) {
      this.lastLeadId = leadId;
      this.displaySnapshot = {};
    }

    // Normalizar: la API de leads devuelve "partner" y "user", la vista espera "contactPartner" y "comercialUser" (como opportunities-content)
    const leadAny = this.lead as any;
    if (!leadAny.contactPartner && leadAny.partner) {
      leadAny.contactPartner = leadAny.partner;
    }
    if (!leadAny.comercialUser && leadAny.user?.partner) {
      leadAny.comercialUser = leadAny.user.partner;
    }

    const setOrKeep = (
      key: keyof typeof this.displaySnapshot,
      computed: string,
      setter: (v: string) => void
    ): void => {
      if (computed !== '-' && computed.trim() !== '') {
        this.displaySnapshot[key] = computed;
        setter(computed);
      } else if (isSameLead && this.displaySnapshot[key] != null) {
        setter(this.displaySnapshot[key]!);
      } else {
        setter(computed);
      }
    };

    // Lead name: SIEMPRE del row raíz (ej. "Lead CEC 6"). No usar partner.name como nombre del lead.
    const contactNameForCompare =
      this.lead?.contactPartner || (this.lead as any)?.partner
        ? `${
            (this.lead.contactPartner || (this.lead as any).partner).name || ''
          } ${
            (this.lead.contactPartner || (this.lead as any).partner).surName ||
            ''
          }`.trim()
        : '';
    const rootName = this.lead?.name;
    const nameIsContact =
      rootName &&
      contactNameForCompare &&
      String(rootName).trim() === contactNameForCompare;
    const rawName =
      (rootName && !nameIsContact ? rootName : null) ||
      this.lead?.oportunidad ||
      this.lead?.originalOpportunityName ||
      (this.lead as any)?.opportunityName ||
      (this.lead as any)?.title;
    const nameStr =
      rawName && String(rawName).trim() !== '' && String(rawName) !== '-'
        ? String(rawName).trim()
        : '-';
    if (nameStr !== '-') {
      this.displaySnapshot.leadName = nameStr;
      this.leadName = nameStr;
      this.leadNameForInput = nameStr;
    } else if (isSameLead && this.displaySnapshot.leadName != null) {
      this.leadName = this.displaySnapshot.leadName;
      this.leadNameForInput = this.leadName;
    } else {
      this.leadName = '-';
      this.leadNameForInput = '';
    }

    // Current lead ID
    this.currentLeadId = leadId;

    setOrKeep(
      'companyName',
      this.getCompanyNameValue(this.lead?.companyId),
      (v) => {
        this.companyName = v;
      }
    );
    setOrKeep('contactName', this.getContactNameValue(), (v) => {
      this.contactName = v;
    });
    setOrKeep(
      'intermediaryCompanyName',
      this.getIntermediaryCompanyNameValue(this.lead?.intermediaryCompanyId),
      (v) => {
        this.intermediaryCompanyName = v;
      }
    );
    setOrKeep(
      'costCenterName',
      this.getCostCenterNameValue(this.lead?.costCenterId),
      (v) => {
        this.costCenterName = v;
      }
    );
    setOrKeep('commercialName', this.getCommercialNameValue(), (v) => {
      this.commercialName = v;
    });
    setOrKeep('salesTeamName', this.getSalesTeamNameValue(), (v) => {
      this.salesTeamName = v;
    });
    setOrKeep('stateName', this.getStateNameValue(this.lead?.stateId), (v) => {
      this.stateName = v;
    });
    setOrKeep(
      'countryName',
      this.getCountryNameValue(this.lead?.countryId),
      (v) => {
        this.countryName = v;
      }
    );
    setOrKeep(
      'referencedByName',
      this.getReferencedByNameValue(this.lead?.referencedById),
      (v) => {
        this.referencedByName = v;
      }
    );

    // Priority, tags (siempre desde datos actuales, no snapshot)
    this.priorityName = this.getPriorityNameValue();
    this.priorityStars = this.getPriorityStarsValue();
    this.leadTags = this.getLeadTagsValue();
    this.tagIds = this.getTagIdsValue();

    // Stages
    this.stagesForTeam = this.getStagesForTeamValue();
    this.currentStageName = this.getStageName(this.lead?.stageId);

    // Filtrar comerciales por equipo
    this.filterUsersByTeam(this.lead?.teamId);

    // Empresa cliente = Partner id (companiesList) únicamente. NO usar lead.company (es Compañía CRM / companyId).
    if (
      this.lead?.partnerId &&
      leadAny.clientCompanyId == null &&
      !this.lead?.clientCompany
    ) {
      const contactPartner =
        this.lead.contactPartner ||
        this.globalValuesService.partnerlist.find(
          (p: Partner) => p.id === this.lead.partnerId
        );
      if (contactPartner?.parentId) {
        const company = this.companiesList.find(
          (c: Partner) => c.id === contactPartner.parentId
        );
        if (company) {
          this.lead.clientCompany = company.name;
          leadAny.clientCompanyId = company.id;
          this.loadContactsByCompany(company.id);
        }
      }
    }
    if (this.lead?.clientCompany) {
      // Cargar contactos de la empresa cliente actual (para selector y filtro)
      const companyId = this.getClientCompanyId(this.lead.clientCompany);
      if (companyId) {
        this.loadContactsByCompany(companyId);
      }
    }

    this.cdr.detectChanges();
  }

  // Helper methods para valores computados
  private getContactNameValue(): string {
    if (this.lead?.contactPartner) {
      const n = `${this.lead.contactPartner.name || ''} ${
        this.lead.contactPartner.surName || ''
      }`.trim();
      if (n) return n;
    }
    const nestedPartner = (this.lead as any)?.partner;
    if (nestedPartner) {
      const n = `${nestedPartner.name || ''} ${
        nestedPartner.surName || ''
      }`.trim();
      if (n) return n;
    }
    if (this.lead?.partnerId) {
      const partners = this.globalValuesService.partnerlist || [];
      const partner = partners.find((p: any) => p.id === this.lead.partnerId);
      if (partner) {
        const n = `${partner.name || ''} ${partner.surName || ''}`.trim();
        if (n) return n;
      }
    }
    return '-';
  }

  private getCommercialNameValue(): string {
    if (this.lead?.comercialUser) {
      const n = `${this.lead.comercialUser.name || ''} ${
        this.lead.comercialUser.surName || ''
      }`.trim();
      if (n) return n;
    }
    const nestedUser = (this.lead as any)?.user;
    if (nestedUser) {
      const p = nestedUser.partner || nestedUser;
      const n = `${p?.name || ''} ${p?.surName || ''}`.trim();
      if (n) return n;
    }
    if (this.lead?.userId) {
      const users = this.globalValuesService.users$.value || [];
      const user = users.find((u: any) => u.id === this.lead.userId);
      if (user?.partner) {
        const n = `${user.partner.name || ''} ${
          user.partner.surName || ''
        }`.trim();
        if (n) return n;
      }
    }
    return '-';
  }

  private getCompanyNameValue(companyId: number | null): string {
    const nested = (this.lead as any)?.company?.name;
    if (nested && String(nested).trim()) return String(nested).trim();
    if (companyId === null || companyId === undefined) return '-';
    const list = this.companies?.length
      ? this.companies
      : this.globalValuesService.companies$.value || [];
    const company = list.find((c: Company) => c.id === companyId);
    return company?.name || '-';
  }

  private getSalesTeamNameValue(): string {
    const nested =
      (this.lead as any)?.salesTeam?.name || (this.lead as any)?.team?.name;
    if (nested && String(nested).trim()) return String(nested).trim();
    const teamId = this.lead?.teamId;
    if (teamId === null || teamId === undefined) return '-';
    const salesTeams = this.globalValuesService.salesTeams$.value || [];
    const team = salesTeams.find((t: any) => t.id === teamId);
    return team?.name || '-';
  }

  /** teamId como número para nz-select (evita que no se muestre la selección si el API devuelve string) */
  getTeamIdForSelect(): number | null {
    const v = this.lead?.teamId;
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return isNaN(n) ? null : n;
  }

  private getPriorityNameValue(): string {
    const priority = this.lead?.priority;
    if (priority === null || priority === undefined) return '-';

    if (typeof priority === 'string') {
      const priorityMap: { [key: string]: string } = {
        Low: 'Baja',
        Medium: 'Media',
        High: 'Alta',
        VeryHigh: 'Muy Alta',
      };
      return priorityMap[priority] || priority;
    }
    return '-';
  }

  private getPriorityStarsValue(): boolean[] {
    const priority = this.lead?.priority;
    if (priority === 'VeryHigh') return [true, true, true, true];
    if (priority === 'High') return [true, true, true, false];
    if (priority === 'Medium') return [true, true, false, false];
    if (priority === 'Low') return [true, false, false, false];
    return [false, false, false, false];
  }

  private getLeadTagsValue(): CrmTag[] {
    if (!this.lead?.tags) return [];
    if (Array.isArray(this.lead.tags)) {
      if (this.lead.tags.length === 0) return [];
      if (typeof this.lead.tags[0] === 'object') {
        return this.lead.tags;
      } else {
        return this.tags.filter((tag: CrmTag) =>
          this.lead.tags.includes(tag.id)
        );
      }
    }
    return [];
  }

  private getTagIdsValue(): number[] {
    if (!this.lead?.tags) return [];
    if (Array.isArray(this.lead.tags)) {
      if (this.lead.tags.length === 0) return [];
      if (typeof this.lead.tags[0] === 'object') {
        return this.lead.tags.map((tag: any) => tag.id || tag).filter(Boolean);
      } else {
        return this.lead.tags;
      }
    }
    return [];
  }

  private getStateNameValue(stateId: number | null): string {
    const nested = (this.lead as any)?.state?.name;
    if (nested && String(nested).trim()) return String(nested).trim();
    if (stateId === null || stateId === undefined) return '-';
    const list = this.states?.length
      ? this.states
      : this.globalValuesService.states$.value || [];
    const state = list.find((s: any) => s.id === stateId);
    return state?.name || '-';
  }

  private getCountryNameValue(countryId: number | null): string {
    const nested = (this.lead as any)?.country?.name;
    if (nested && String(nested).trim()) return String(nested).trim();
    if (countryId === null || countryId === undefined) return '-';
    const list = this.countries?.length
      ? this.countries
      : this.globalValuesService.countries$.value || [];
    const country = list.find((c: any) => c.id === countryId);
    return country?.name || '-';
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString as string);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return '-';
    }
  }

  // Métodos de edición (delegados al padre)
  isFieldEditing(fieldPath: string): boolean {
    return this.currentEditingField === fieldPath;
  }

  onFieldClick(fieldPath: string, event: MouseEvent): void {
    if (!this.editPermissions) return; // Sin permisos: no hacer nada; el enlace redirigirá
    // En modo edición: evitar que el enlace abra la URL y abrir Quill
    if ((event.target as HTMLElement)?.closest?.('a')) {
      event.preventDefault();
    }
    event.stopPropagation();
    this.startEditingField(fieldPath);
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.currentEditingField) return;

    const target = event.target as HTMLElement | null;
    const activeElement = document.activeElement as HTMLElement | null;
    const activeFieldWillBlur = shouldDeferLeadFieldCloseToBlur(
      activeElement,
      this.currentEditingField,
    );

    if (activeFieldWillBlur) return;

    const clickedInsideCurrentField = !!target?.closest?.(
      `[data-field="${this.currentEditingField}"], .ql-editor, .ql-toolbar`,
    );
    const clickedInsideOverlay = !!target?.closest?.(
      '.ant-select-dropdown, .ant-picker-dropdown',
    );

    if (!clickedInsideCurrentField && !clickedInsideOverlay) {
      this.stopEditingField(this.currentEditingField);
      this.cdr.detectChanges();
    }
  }

  @HostListener('document:keydown.enter', ['$event'])
  onDocumentEnter(event: KeyboardEvent): void {
    if (!this.currentEditingField) return;

    const activeElement = document.activeElement as HTMLElement | null;
    const activeInput = activeElement?.matches?.(
      `input[data-field="${this.currentEditingField}"], textarea[data-field="${this.currentEditingField}"]`,
    );

    if (activeInput && activeElement) {
      event.preventDefault();
      activeElement.blur();
    }
  }

  onFieldBlur(fieldPath: string, event: FocusEvent): void {
    if (this.currentEditingField === fieldPath) {
      // Obtener el valor del input desde el evento
      const target = event.target as HTMLInputElement;
      const newValue = target?.value || '';
      const previousValue =
        fieldPath === 'name'
          ? this.lead?.oportunidad ||
            this.lead?.originalOpportunityName ||
            this.lead?.name
          : this.lead?.[fieldPath];

      // Actualizar el valor en el objeto lead
      if (this.lead) {
        if (fieldPath === 'name') {
          // Para el nombre del lead, actualizar oportunidad, originalOpportunityName y name
          this.lead.oportunidad = newValue;
          this.lead.originalOpportunityName = newValue;
          this.lead.name = newValue;
        } else {
          this.lead[fieldPath] = newValue;
        }
      }

      // Emitir el evento de actualización
      this.fieldUpdate.emit({
        path: fieldPath,
        value: newValue,
        previousValue,
      });
      this.stopEditingField(fieldPath);
      this.updateComputedProperties();
    }
  }

  onSelectChange(fieldPath: string, value: any): void {
    if (this.currentEditingField === fieldPath) {
      // Actualizar el valor en el objeto lead
      if (this.lead) {
        // Si es clientCompany, guardar el nombre de la empresa (string) en lugar del ID
        if (fieldPath === 'clientCompany' && value) {
          const selectedCompany = this.companiesList.find(
            (c: Partner) => c.id === value
          );
          if (selectedCompany) {
            this.lead[fieldPath] = selectedCompany.name;
            (this.lead as any).clientCompanyId = selectedCompany.id;

            this.lead.partnerId = null;
            this.lead.contactPartner = null;
            (this.lead as any).partner = null;
            this.lead.email = null;
            this.lead.phone = null;
            this.displaySnapshot.contactName = undefined;
            this.contactName = '-';
            this.loadContactsByCompany(value);
            this.fieldUpdate.emit({ path: 'partnerId', value: null });
            this.fieldUpdate.emit({ path: 'email', value: null });
            this.fieldUpdate.emit({ path: 'phone', value: null });
            this.updateComputedProperties();
          } else {
            this.lead[fieldPath] = value;
          }
        } else if (fieldPath === 'clientCompany' && !value) {
          this.lead[fieldPath] = null;
          (this.lead as any).clientCompanyId = null;
          this.lead.partnerId = null;
          this.lead.contactPartner = null;
          (this.lead as any).partner = null;
          this.lead.email = null;
          this.lead.phone = null;
          this.contactList = [];
          this.displaySnapshot.contactName = undefined;
          this.contactName = '-';
          this.fieldUpdate.emit({ path: 'partnerId', value: null });
          this.fieldUpdate.emit({ path: 'email', value: null });
          this.fieldUpdate.emit({ path: 'phone', value: null });
          this.updateComputedProperties();
        } else {
          this.lead[fieldPath] = value;
        }

        // Asegurar que teamId se guarde como número para el payload y para que el select muestre bien la selección
        if (fieldPath === 'teamId') {
          const numVal =
            value !== null && value !== undefined && value !== ''
              ? Number(value)
              : null;
          this.lead.teamId = numVal;
          if (numVal != null) {
            (this.lead as any).team =
              this.globalValuesService.salesTeams$.value?.find(
                (t: any) => t.id === numVal
              ) ?? (this.lead as any).team;
          } else {
            (this.lead as any).team = null;
          }
        }

        // Si es intermediaryCompanyId, actualizar también el objeto anidado para que el nombre se muestre al instante
        if (fieldPath === 'intermediaryCompanyId') {
          const company = value
            ? this.companiesList.find((c: Partner) => c.id === value)
            : null;
          (this.lead as any).intermediaryCompany = company
            ? { id: company.id, name: company.name }
            : null;
        }

        // Si es referencedById, actualizar también el objeto anidado para que el nombre se muestre al instante
        if (fieldPath === 'referencedById' && value) {
          const partner = this.globalValuesService.partnerlist.find(
            (p: any) => p.id === value
          );
          if (partner) {
            (this.lead as any).referencedByPartner = partner;
          }
        } else if (fieldPath === 'referencedById' && !value) {
          (this.lead as any).referencedByPartner = null;
        }

        // Si es partnerId, actualizar también el objeto contactPartner para que el avatar se muestre correctamente
        if (fieldPath === 'partnerId' && value) {
          // Buscar el contacto en contactList primero, si no está ahí buscar en partnerlist
          const partner =
            this.contactList.find((p: any) => p.id === value) ||
            this.globalValuesService.partnerlist.find(
              (p: any) => p.id === value
            );

          if (partner) {
            this.lead.contactPartner = partner;

            // Actualizar email y teléfono del contacto
            const partnerEmail = partner.email || null;
            const partnerPhone = partner.phone || null;

            this.lead.email = partnerEmail;
            this.lead.phone = partnerPhone;

            // Emitir eventos de actualización para email y phone
            this.fieldUpdate.emit({ path: 'email', value: partnerEmail });
            this.fieldUpdate.emit({ path: 'phone', value: partnerPhone });
          }
        }

        // Si es userId, actualizar también el objeto comercialUser para que el avatar se muestre correctamente
        if (fieldPath === 'userId' && value) {
          const users = this.globalValuesService.users$.value || [];
          const user = users.find((u: any) => u.id === value);
          if (user?.partner) {
            this.lead.comercialUser = user.partner;
          }
        }

        if (fieldPath === 'teamId') {
          // Guardar backups de campos relacionados ANTES de cambiar el equipo
          if (
            this.backupFieldValues['userId'] === undefined &&
            this.lead?.userId
          ) {
            this.backupFieldValues['userId'] = this.lead.userId;
          }

          // Filtrar los comerciales disponibles para el nuevo equipo (usar this.lead.teamId ya normalizado a número)
          const teamIdNum =
            this.lead?.teamId != null ? Number(this.lead.teamId) : null;
          this.filterUsersByTeam(teamIdNum);

          // Verificar si el comercial actual pertenece al nuevo equipo; si no, limpiar para elegir uno del equipo
          if (teamIdNum && this.lead?.userId) {
            const belongsToNewTeam = this.filteredUsersForTeam.some(
              (u: any) => u.id === this.lead.userId
            );
            if (!belongsToNewTeam) {
              const previousUserId = this.lead.userId;
              this.lead.userId = null;
              this.lead.comercialUser = null;
              (this.lead as any).user = null;
              this.displaySnapshot.commercialName = undefined;
              this.commercialName = '-';
              this.fieldUpdate.emit({
                path: 'userId',
                value: null,
                previousValue: previousUserId,
              });
            }
          } else if (!teamIdNum) {
            // Si se limpia el equipo, limpiar también el comercial
            const previousUserId = this.lead?.userId;
            this.lead.userId = null;
            this.lead.comercialUser = null;
            (this.lead as any).user = null;
            this.displaySnapshot.commercialName = undefined;
            this.commercialName = '-';
            if (previousUserId) {
              this.fieldUpdate.emit({
                path: 'userId',
                value: null,
                previousValue: previousUserId,
              });
            }
          }
        }
      }

      // Emitir el evento de actualización (número para teamId, nombre para clientCompany)
      let emitValue: any = value;
      if (fieldPath === 'clientCompany' && value) {
        emitValue =
          this.companiesList.find((c: Partner) => c.id === value)?.name ||
          value;
      } else if (fieldPath === 'teamId') {
        emitValue = this.lead?.teamId != null ? Number(this.lead.teamId) : null;
      }
      this.fieldUpdate.emit({ path: fieldPath, value: emitValue });
      this.stopEditingField(fieldPath);
      this.updateComputedProperties();
    }
  }

  // Método para cargar contactos filtrados por empresa cliente
  loadContactsByCompany(companyId: number): void {
    if (!companyId) {
      this.contactList = [];
      return;
    }

    const payload = {
      filterGroups: [
        [
          { property: 'isCompany', value: 'false', operator: 'Equals' },
          {
            property: 'parentId',
            value: String(companyId),
            operator: 'Equals',
          },
        ],
      ],
      startRow: 0,
      endRow: 99999999,
      sortDirection: 'asc',
      sortProperty: 'name',
    };

    this.contactLoading = true;
    this.partnerService
      .postAdvanced(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.contactLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (rows: Partner[]) => {
          this.contactList = rows || [];
          this.cdr.detectChanges();
        },
        error: () => {
          this.contactList = [];
          this.cdr.detectChanges();
        },
      });
  }

  /** Texto a mostrar en Empresa Cliente: mapeado desde Partner (companiesList) por clientCompanyId o nombre */
  getLeadClientCompanyDisplay(): string {
    const leadAny = this.lead as any;
    const partnerId = leadAny?.clientCompanyId;
    if (partnerId != null) {
      const company = this.companiesList.find(
        (c: Partner) => c.id === partnerId
      );
      if (company?.name) return String(company.name).trim();
    }
    const c = this.lead?.clientCompany;
    if (c && String(c).trim()) return String(c).trim();
    if (this.lead?.partnerId) {
      const contactPartner =
        this.lead.contactPartner ||
        this.globalValuesService.partnerlist?.find(
          (p: Partner) => p.id === this.lead.partnerId
        );
      if (contactPartner?.parentId) {
        const company = this.companiesList.find(
          (co: Partner) => co.id === contactPartner.parentId
        );
        if (company?.name) return String(company.name).trim();
      }
    }
    return '-';
  }

  /** ID de la empresa cliente = Partner id de companiesList (para editar y cargar en detalle) */
  getClientCompanyId(_clientCompanyName?: string | null): number | null {
    const leadAny = this.lead as any;
    if (leadAny?.clientCompanyId != null) return leadAny.clientCompanyId;
    if (this.lead?.partnerId) {
      const contactPartner =
        this.lead.contactPartner ||
        this.globalValuesService.partnerlist?.find(
          (p: Partner) => p.id === this.lead.partnerId
        );
      if (contactPartner?.parentId) {
        const company = this.companiesList.find(
          (c: Partner) => c.id === contactPartner.parentId
        );
        if (company) return company.id;
      }
    }
    if (this.lead?.clientCompany) {
      const company = this.companiesList.find(
        (c: Partner) => c.name === this.lead.clientCompany
      );
      if (company) return company.id;
    }
    return null;
  }

  // Helper para obtener el nombre de la empresa intermediaria
  private getIntermediaryCompanyNameValue(
    intermediaryCompanyId: number | null
  ): string {
    const nested = (this.lead as any)?.intermediaryCompany;
    if (nested?.name && String(nested.name).trim()) {
      if (nested.id === intermediaryCompanyId || !intermediaryCompanyId)
        return String(nested.name).trim();
    }
    if (intermediaryCompanyId === null || intermediaryCompanyId === undefined)
      return '-';
    const company = this.companiesList.find(
      (c: Partner) => c.id === intermediaryCompanyId
    );
    return company?.name || '-';
  }

  // Helper para obtener el nombre del centro de costo
  private getCostCenterNameValue(costCenterId: number | null): string {
    const nested = (this.lead as any)?.costCenter?.name;
    if (nested && String(nested).trim()) return String(nested).trim();
    if (costCenterId === null || costCenterId === undefined) return '-';
    const list = this.costCenters?.length
      ? this.costCenters
      : this.globalValuesService.costCenters$.value || [];
    const costCenter = list.find((cc: any) => cc.id === costCenterId);
    return costCenter?.name || '-';
  }

  // Helper para obtener el nombre del partner referido
  private getReferencedByNameValue(referencedById: number | null): string {
    const nested = (this.lead as any)?.referencedByPartner;
    if (nested) {
      const n = `${nested.name || ''} ${nested.surName || ''}`.trim();
      if (n) return n;
    }
    if (referencedById === null || referencedById === undefined) return '-';
    const partners = this.globalValuesService.partnerlist || [];
    const partner = partners.find((p: any) => p.id === referencedById);
    if (partner) {
      const n = `${partner.name || ''} ${partner.surName || ''}`.trim();
      if (n) return n;
    }
    return '-';
  }

  onDateChange(fieldPath: string, value: Date | null): void {
    if (this.currentEditingField === fieldPath) {
      const previousValue = this.backupFieldValues[fieldPath]; // Obtener el valor anterior
      const dateValue = value ? value.toISOString() : null;

      // Actualizar el valor en el objeto lead
      if (this.lead) {
        this.lead[fieldPath] = dateValue;
      }

      // Limpiar error del campo al editar nuevamente
      if (this.fieldErrors[fieldPath]) {
        delete this.fieldErrors[fieldPath];
      }

      // Emitir el evento de actualización
      this.fieldUpdate.emit({
        path: fieldPath,
        value: dateValue,
        previousValue: previousValue,
      });
      this.stopEditingField(fieldPath);
      this.updateComputedProperties();
    }
  }

  onArrayChange(fieldPath: string, value: any[]): void {
    if (this.currentEditingField === fieldPath) {
      this.tempFieldValues[fieldPath] = value;
    }
  }

  onArraySelectClose(fieldPath: string, isOpen: boolean): void {
    if (!isOpen && this.currentEditingField === fieldPath) {
      const previousValue = this.backupFieldValues[fieldPath]; // Obtener el valor anterior
      const value = this.tempFieldValues[fieldPath] || this.getTagIdsValue();

      // Actualizar el valor en el objeto lead
      if (this.lead) {
        this.lead[fieldPath] = value;
      }

      // Limpiar error del campo al editar nuevamente
      if (this.fieldErrors[fieldPath]) {
        delete this.fieldErrors[fieldPath];
      }

      // Emitir el evento de actualización
      this.fieldUpdate.emit({
        path: fieldPath,
        value,
        previousValue: previousValue,
      });
      this.stopEditingField(fieldPath);
      this.updateComputedProperties();
    }
  }

  private startEditingField(fieldPath: string): void {
    if (this.currentEditingField && this.currentEditingField !== fieldPath) {
      this.stopEditingField(this.currentEditingField);
    }

    const backupValue = this.lead?.[fieldPath];
    this.backupFieldValues[fieldPath] = backupValue;

    // Si se está editando el equipo, guardar también los backups de campos relacionados
    if (fieldPath === 'teamId') {
      if (this.backupFieldValues['userId'] === undefined && this.lead?.userId) {
        this.backupFieldValues['userId'] = this.lead.userId;
        console.log('💾 Backup guardado para userId:', this.lead.userId);
      }
    }

    this.currentEditingField = fieldPath;

    // Inicializar valor editable para campos especiales
    if (fieldPath === 'description') {
      this.editingFieldValue = this.lead?.description || '';
    }

    if (fieldPath === 'tags') {
      this.tempFieldValues[fieldPath] = this.getTagIdsValue();
    }

    // Inicializar el valor del input para el campo 'name'
    if (fieldPath === 'name') {
      // Obtener el nombre actual del lead (similar a updateComputedProperties)
      const currentName =
        this.lead?.oportunidad ||
        this.lead?.originalOpportunityName ||
        this.lead?.name ||
        '';
      this.leadNameForInput = currentName;
    }

    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  saveEditingField(fieldPath: string): void {
    if (!this.lead) return;
    const previousValue = this.backupFieldValues[fieldPath];
    // Para description (Quill) tomar SIEMPRE el HTML actual del editor si está disponible.
    // Esto evita guardar el valor viejo cuando el usuario pulsa Guardar sin "cerrar" el editor
    // y el ngModel aún no se sincronizó al último carácter.
    const newValue =
      fieldPath === 'description'
        ? this.leadQuillEditor?.quillEditor?.root?.innerHTML ??
          this.editingFieldValue ??
          ''
        : this.lead[fieldPath];

    // Actualizar lead y emitir evento
    this.lead[fieldPath] = newValue;
    if (this.fieldErrors[fieldPath]) delete this.fieldErrors[fieldPath];
    this.fieldUpdate.emit({ path: fieldPath, value: newValue, previousValue });
    this.stopEditingField(fieldPath);
    this.updateComputedProperties();
  }

  cancelEditingField(): void {
    const fieldPath = this.currentEditingField;
    if (!fieldPath) return;
    this.cancelFieldEdit(fieldPath);
    // Restaurar valor editable si aplica
    if (fieldPath === 'description') {
      this.editingFieldValue = this.backupFieldValues[fieldPath] || '';
    }
  }

  onEditorSelectionChanged(event: any): void {
    // placeholder: reserved for future behavior (e.g., toolbar state)
  }

  showLinkModal(
    currentUrl: string,
    quill: any,
    range: { index: number; length: number }
  ): void {
    this.isAddingLink = true;
    this.linkModalUrl = currentUrl || '';
    this.linkModalQuill = quill;
    this.linkModalRange = range;
    this.cdr.detectChanges();
    this.linkModalRef = this.modalService.create({
      nzTitle: currentUrl ? 'Editar enlace' : 'Agregar enlace',
      nzContent: this.linkModalTemplate,
      nzFooter: null,
      nzViewContainerRef: this.viewContainerRef,
      nzData: { component: this },
      nzBodyStyle: { padding: '24px' },
      nzCentered: true,
      nzWidth: 500,
      nzClosable: true,
      nzMaskClosable: false,
    });
    this.linkModalRef.afterClose?.subscribe?.(() => {
      setTimeout(() => {
        this.isAddingLink = false;
      }, 150);
    });
    setTimeout(() => {
      if (this.linkInput?.nativeElement) {
        this.linkInput.nativeElement.focus();
        this.linkInput.nativeElement.select();
      }
    }, 200);
  }

  closeLinkModal(): void {
    this.linkModalRef?.close();
    this.linkModalQuill = null;
    this.linkModalRange = null;
    this.linkModalUrl = '';
    this.isAddingLink = false;
  }

  /** Quita el formato de enlace del texto seleccionado y lo deja como texto normal. */
  removeLinkFromSelection(): void {
    if (!this.linkModalQuill || !this.linkModalRange) {
      this.closeLinkModal();
      return;
    }
    this.linkModalRef?.close();
    setTimeout(() => {
      if (!this.linkModalQuill || !this.linkModalRange) {
        this.linkModalQuill = null;
        this.linkModalRange = null;
        this.linkModalUrl = '';
        this.isAddingLink = false;
        return;
      }
      this.linkModalQuill.focus();
      this.linkModalQuill.setSelection(
        this.linkModalRange.index,
        this.linkModalRange.length
      );
      this.linkModalQuill.format('link', false);
      if (this.linkModalQuill.root) {
        const html = this.linkModalQuill.root.innerHTML;
        this.editingFieldValue = html;
        if (this.lead) this.lead.description = html;
        this.cdr.detectChanges();
      }
      this.linkModalQuill = null;
      this.linkModalRange = null;
      this.linkModalUrl = '';
      this.isAddingLink = false;
    }, 100);
  }

  onLinkModalEnter(): void {
    this.onLinkModalConfirm();
  }

  private normalizeLinkUrl(url: string): string {
    const u = url.trim();
    if (!u) return u;
    if (/^https?:\/\//i.test(u)) return u;
    return 'https://' + u;
  }

  onLinkModalConfirm(): void {
    const url = (this.linkModalUrl || '').trim();
    if (!url || !this.linkModalQuill || !this.linkModalRange) {
      this.closeLinkModal();
      return;
    }
    const normalized = this.normalizeLinkUrl(url);
    this.linkModalRef?.close();
    setTimeout(() => {
      if (!this.linkModalQuill || !this.linkModalRange) {
        this.linkModalQuill = null;
        this.linkModalRange = null;
        this.linkModalUrl = '';
        this.isAddingLink = false;
        return;
      }
      this.linkModalQuill.focus();
      this.linkModalQuill.setSelection(
        this.linkModalRange.index,
        this.linkModalRange.length
      );
      this.linkModalQuill.format('link', normalized);
      if (this.linkModalQuill.root) {
        const html = this.linkModalQuill.root.innerHTML;
        this.editingFieldValue = html;
        if (this.lead) this.lead.description = html;
        this.cdr.detectChanges();
      }
      this.linkModalQuill = null;
      this.linkModalRange = null;
      this.linkModalUrl = '';
      this.isAddingLink = false;
    }, 100);
  }

  private stopEditingField(fieldPath: string): void {
    if (this.currentEditingField === fieldPath) {
      this.currentEditingField = null;
      delete this.backupFieldValues[fieldPath];
      delete this.tempFieldValues[fieldPath];
    }
  }

  onEditOverlayOpenChange(fieldPath: string, isOpen: boolean | Event): void {
    const overlayIsOpen = typeof isOpen === 'boolean' ? isOpen : false;
    if (!overlayIsOpen) {
      setTimeout(() => {
        if (this.currentEditingField === fieldPath) {
          this.stopEditingField(fieldPath);
          this.cdr.detectChanges();
        }
      }, 0);
    }
  }

  cancelFieldEdit(fieldPath: string): void {
    if (this.backupFieldValues[fieldPath] !== undefined) {
      if (this.lead) {
        const backupValue = this.backupFieldValues[fieldPath];
        this.lead[fieldPath] = backupValue;

        // Si se cancela el cambio de teamId, restaurar también los campos relacionados
        if (fieldPath === 'teamId') {
          // Restaurar el equipo
          this.lead.teamId = backupValue;

          // Filtrar comerciales para el equipo restaurado
          this.filterUsersByTeam(backupValue);

          // Restaurar userId si hay backup y pertenece al equipo restaurado
          const userIdBackup = this.backupFieldValues['userId'];
          if (userIdBackup !== undefined && userIdBackup !== null) {
            const belongsToRestoredTeam = this.filteredUsersForTeam.some(
              (u: any) => u.id === userIdBackup
            );
            if (belongsToRestoredTeam) {
              // Restaurar el userId si pertenece al equipo restaurado
              this.lead.userId = userIdBackup;
              const users = this.globalValuesService.users$.value || [];
              const user = users.find((u: any) => u.id === userIdBackup);
              if (user?.partner) {
                this.lead.comercialUser = user.partner;
              }
            } else {
              // Si no pertenece, limpiar el userId
              this.lead.userId = null;
              this.lead.comercialUser = null;
            }
          }
        }
      }
    }
    // Limpiar error del campo cuando se cancela su edición
    if (this.fieldErrors[fieldPath]) {
      delete this.fieldErrors[fieldPath];
    }

    // Limpiar backups de campos relacionados después de restaurar
    if (fieldPath === 'teamId') {
      delete this.backupFieldValues['userId'];
    }

    this.stopEditingField(fieldPath);
    this.updateComputedProperties();
  }

  removeTag(tagId: number, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.lead?.tags) return;
    const currentTags = Array.isArray(this.lead.tags)
      ? [...this.lead.tags]
      : [];
    const filteredTags = currentTags.filter((tag: any) => {
      if (typeof tag === 'object') {
        return tag.id !== tagId;
      }
      return tag !== tagId;
    });

    // Convertir a array de IDs
    const tagIds = filteredTags.map((tag: any) =>
      typeof tag === 'object' ? tag.id : tag
    );

    // Actualizar el valor en el objeto lead
    if (this.lead) {
      this.lead.tags = tagIds;
    }

    // Emitir el evento de actualización
    this.fieldUpdate.emit({ path: 'tags', value: tagIds });
    this.updateComputedProperties();
  }

  // Helper para mostrar dirección completa
  getAddressDisplay(): string {
    const parts: string[] = [];
    if (this.lead?.street) parts.push(this.lead.street);
    if (this.lead?.street2) parts.push(this.lead.street2);
    if (this.lead?.city) parts.push(this.lead.city);
    if (this.lead?.province || this.stateName !== '-')
      parts.push(this.lead?.province || this.stateName);
    if (this.lead?.zip) parts.push(this.lead.zip);
    if (this.countryName !== '-') parts.push(this.countryName);
    return parts.length > 0 ? parts.join(', ') : '-';
  }

  // Helper para verificar si hay campos de dirección editándose
  isAddressFieldEditing(): boolean {
    return (
      this.isFieldEditing('street') ||
      this.isFieldEditing('street2') ||
      this.isFieldEditing('city') ||
      this.isFieldEditing('province') ||
      this.isFieldEditing('zip') ||
      this.isFieldEditing('countryId') ||
      this.isFieldEditing('stateId')
    );
  }

  // Stage methods
  private getStagesForTeamValue(): any[] {
    const teamId = this.lead?.teamId;
    const allStages = this.globalValuesService.stages$.value;
    let filteredStages: any[] = [];

    if (teamId) {
      filteredStages = allStages.filter((stage: any) =>
        this.stageBelongsToTeam(stage, teamId)
      );
    } else {
      filteredStages = allStages.filter((stage: any) => this.stageBelongsToTeam(stage, teamId));
    }

    if (filteredStages.length === 0) {
      filteredStages = allStages;
    }

    return filteredStages.sort(
      (a: any, b: any) => (a.order || 0) - (b.order || 0)
    );
  }

  private getStageTeamIds(stage: any): Array<number | string> {
    if (Array.isArray(stage?.teamIds)) {
      return stage.teamIds
        .map((team: any) => team?.id ?? team)
        .filter((stageTeamId: any) => stageTeamId !== null && stageTeamId !== undefined);
    }

    if (Array.isArray(stage?.teams)) {
      return stage.teams
        .map((team: any) => team?.id ?? team)
        .filter((stageTeamId: any) => stageTeamId !== null && stageTeamId !== undefined);
    }

    return [];
  }

  private stageBelongsToTeam(stage: any, teamId: number | string | null | undefined): boolean {
    if (stage?.isGlobal === true) {
      return true;
    }

    const stageTeamIds = this.getStageTeamIds(stage);

    if (teamId === null || teamId === undefined || teamId === '') {
      return stageTeamIds.length === 0;
    }

    return stageTeamIds.some((stageTeamId) => Number(stageTeamId) === Number(teamId));
  }

  private getStageName(stageId: number | null): string {
    if (stageId === null || stageId === undefined) return 'Nuevo';
    const stage = this.stages.find((s: any) => s.id === stageId);
    return stage?.name || 'Nuevo';
  }

  // Filtrar comerciales por equipo de ventas
  filterUsersByTeam(teamId: number | null | undefined): void {
    if (!teamId) {
      // Si no hay equipo seleccionado, no mostrar usuarios
      this.filteredUsersForTeam = [];
      return;
    }

    // Buscar el equipo en la lista de equipos
    const salesTeams = this.globalValuesService.salesTeams$.value || [];
    const selectedTeam = salesTeams.find((team: any) => team.id === teamId);

    if (!selectedTeam) {
      // Si no se encuentra el equipo, no mostrar usuarios
      this.filteredUsersForTeam = [];
      return;
    }

    // Obtener los userIds de los miembros del equipo
    const teamMemberUserIds: number[] = [];

    // Verificar si el equipo tiene memberList o members (usar any para evitar errores de tipo)
    const team = selectedTeam as any;
    const members = team.memberList || team.members || [];

    members.forEach((member: any) => {
      // member puede ser un objeto con userId o solo el id directamente
      if (typeof member === 'object' && member !== null) {
        const userId = member.userId || member.id;
        if (userId != null && userId !== undefined) {
          teamMemberUserIds.push(userId);
        }
      } else if (typeof member === 'number') {
        teamMemberUserIds.push(member);
      }
    });

    // Si no hay miembros, no mostrar usuarios (se mostrará un mensaje en el HTML)
    if (teamMemberUserIds.length === 0) {
      this.filteredUsersForTeam = [];
      return;
    }

    // Filtrar usuarios que pertenecen al equipo
    const allUsers = this.globalValuesService.users$.value || [];
    this.filteredUsersForTeam = allUsers.filter((user: any) =>
      teamMemberUserIds.includes(user.id)
    );
  }

  hasFieldError(fieldPath: string): boolean {
    return !!this.fieldErrors[fieldPath];
  }

  getFieldError(fieldPath: string): string | null {
    return this.fieldErrors[fieldPath] || null;
  }

  isCurrentStage(stageName: string): boolean {
    return this.currentStageName === stageName;
  }

  changeStage(stage: any): void {
    if (!this.editPermissions) return;
    const path = 'stageId';
    const value = stage.id;
    const backupValue = this.lead?.stageId;
    if (backupValue === value || backupValue == value) return;

    if (!this.backupFieldValues[path]) {
      this.backupFieldValues[path] = backupValue;
    }

    // Actualizar el valor en el objeto lead
    if (this.lead) {
      this.lead.stageId = value;
    }

    // Emitir el evento de actualización
    this.fieldUpdate.emit({ path, value, previousValue: backupValue });
    this.updateComputedProperties();
  }

  getStageIcon(index: number): string {
    const icons = [
      'circle',
      'circle',
      'circle',
      'pause',
      'check_circle',
      'cancel',
    ];
    return icons[index] || 'circle';
  }

  getStageColor(index: number): string {
    const colors = [
      '#0072F5',
      '#8E4EC6',
      '#F5A623',
      '#666666',
      '#6CDA75',
      '#E53E3E',
    ];
    return colors[index] || '#666666';
  }

  getStageHoverClass(index: number): string {
    const classes = ['blue-hover', 'purple-hover', 'orange-hover', '', '', ''];
    return classes[index] || '';
  }

  onPartnerCreated(newPartner: Partner): void {
    // Agregar el nuevo partner a la lista de contactos
    if (!this.contactList.find((p) => p.id === newPartner.id)) {
      this.contactList.push(newPartner);
    }
    // Actualizar el lead con el nuevo partner
    if (this.lead) {
      this.lead.partnerId = newPartner.id;
      this.lead.contactPartner = newPartner;
      // Actualizar email y teléfono del contacto
      this.lead.email = newPartner.email || null;
      this.lead.phone = newPartner.phone || null;
      // Emitir eventos de actualización
      this.fieldUpdate.emit({ path: 'partnerId', value: newPartner.id });
      this.fieldUpdate.emit({ path: 'email', value: newPartner.email || null });
      this.fieldUpdate.emit({ path: 'phone', value: newPartner.phone || null });
    }
    // Actualizar propiedades computadas
    this.updateComputedProperties();
  }
}
